import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import { listEngagementDocuments } from './documents';
import { newIdempotencyKey } from './engagement-requirements';

/**
 * Engagement -> Discovery: setup, the guide and dashboard, coverage and the
 * framework version move (T-0185, over the T-0171 API).
 *
 * Contract: `apps/api/src/routes/discovery.ts` and
 * `apps/api/src/services/discovery-setup.ts` / `discovery-coverage.ts`.
 * Everything is under `/api/v1/engagements/:engagement_id/discovery`.
 *
 *   - Every number in these responses is computed on the server by
 *     `@caliber-2/discovery-core` (coverage, the guide, attention cards,
 *     KPIs). The screen shows them; it never counts or estimates.
 *   - `not_found` covers an unknown engagement, a non-member, and an
 *     engagement whose Discovery module is off.
 *   - `:start` and `:move-version` take an `Idempotency-Key`, one per
 *     confirmed action.
 */

const GuideStepSchema = z.object({
  key: z.string(),
  title: z.string(),
  state: z.enum(['done', 'current', 'todo', 'locked']),
  counts: z.record(z.number()),
  unlock_hint: z.string().nullable(),
});
export type GuideStep = z.infer<typeof GuideStepSchema>;

const AttentionItemSchema = z.object({
  kind: z.string(),
  tone: z.enum(['action', 'warn', 'info', 'ok']),
  counts: z.record(z.number()),
  target: z
    .object({
      kind: z.string(),
      interview_id: z.string().optional(),
      person_id: z.string().optional(),
      status: z.string().optional(),
    })
    .nullable(),
  breakdown: z
    .array(
      z.object({
        interview_id: z.string().nullable(),
        code: z.string().nullable(),
        count: z.number(),
      }),
    )
    .optional(),
  state: z.string().nullable().optional(),
});
export type AttentionItem = z.infer<typeof AttentionItemSchema>;

export const DiscoverySummarySchema = z.object({
  started: z.boolean(),
  started_at: z.string().nullable(),
  framework: z
    .object({ id: z.string(), name: z.string(), version: z.number(), version_id: z.string() })
    .nullable(),
  latest_version: z.number().nullable(),
  sources_required: z.number().nullable(),
  corroboration_off: z.boolean(),
  lead: z.array(z.object({ user_id: z.string(), name: z.string() })),
  permissions: z.object({
    can_start: z.boolean(),
    can_move_version: z.boolean(),
    can_edit_client_facts: z.boolean(),
  }),
  client_facts: z.object({ set: z.number(), total: z.number() }),
  mode: z.enum(['guide', 'dashboard']),
  guide: z.object({ current: z.string().nullable(), steps: z.array(GuideStepSchema) }),
  attention: z.array(AttentionItemSchema),
  kpis: z.object({
    corroborated: z.number(),
    partial: z.number(),
    none: z.number(),
    areas: z.number(),
    awaiting_review: z.number(),
    interviews_held: z.number(),
    interviews_total: z.number(),
    interviews_scheduled: z.number(),
  }),
});
export type DiscoverySummary = z.infer<typeof DiscoverySummarySchema>;

const PlanRoleSchema = z.object({
  interview_type_id: z.string(),
  interview_type_code: z.string(),
  interview_type_name: z.string(),
  ordinal: z.number(),
  role: z.string(),
  optional: z.boolean(),
  duration_minutes: z.number(),
});
export type PlanRole = z.infer<typeof PlanRoleSchema>;

const SetupFrameworkSchema = z.object({
  framework: z.object({ id: z.string(), name: z.string() }),
  version: z.object({
    id: z.string(),
    version: z.number(),
    sources_required: z.number(),
    published_at: z.string().nullable(),
  }),
  counts: z.object({
    areas: z.number(),
    interview_types: z.number(),
    questionnaires: z.number(),
    interviews: z.number(),
  }),
  plan: z.array(PlanRoleSchema),
});
export type SetupFramework = z.infer<typeof SetupFrameworkSchema>;

export type MatchDecision =
  | { decision: 'same'; person_id: string }
  | { decision: 'same'; plan_index: number }
  | { decision: 'different'; candidate_ids: string[]; plan_indexes: number[] };

export interface StartPlanRow {
  interview_type_id: string;
  ordinal: number;
  person?: { full_name: string; email?: string; match_decision?: MatchDecision };
  scheduled_at?: string;
}

export interface StartDiscoveryBody {
  framework_id: string;
  plan: StartPlanRow[];
  send_invites?: boolean;
}

const StartResultSchema = z.object({
  framework_id: z.string(),
  framework_version: z.number(),
  interviews: z.array(z.object({ id: z.string(), code: z.string() }).passthrough()),
  people_created: z.number(),
  invites_sent: z.number(),
  invites_failed: z.array(z.object({ interview_code: z.string() }).passthrough()),
});
export type StartResult = z.infer<typeof StartResultSchema>;

/** One plan row the same-person check could not settle (`person.possible_duplicate`). */
export const DuplicateRowSchema = z.object({
  index: z.number(),
  full_name: z.string(),
  candidates: z.array(
    z.object({
      person_id: z.string().optional(),
      plan_index: z.number().optional(),
      full_name: z.string(),
      title: z.string().nullable(),
    }),
  ),
});
export type DuplicateRow = z.infer<typeof DuplicateRowSchema>;

export const NextStepSchema = z.object({
  kind: z.string(),
  sources: z.number().optional(),
  interview_id: z.string().nullable().optional(),
  interview_code: z.string().nullable().optional(),
  source_key: z.string().nullable().optional(),
  progress: z.number().nullable().optional(),
  scheduled_at: z.string().nullable().optional(),
  person_id: z.string().optional(),
});
export type AreaNextStep = z.infer<typeof NextStepSchema>;

export const SourceKeySchema = z.object({
  key: z.string(),
  kind: z.enum(['person', 'document']),
  id: z.string(),
  name: z.string(),
  confirmed_count: z.number(),
});
export type CoverageSource = z.infer<typeof SourceKeySchema>;

export const AreaCoverageSchema = z.object({
  area_id: z.string(),
  code: z.string(),
  label: z.string(),
  name: z.string(),
  status: z.enum(['corroborated', 'partial', 'none']),
  /** "Covered", "Needs N more source(s)", "No evidence yet" (Decision 53.6), worded by the API. */
  status_label: z.string(),
  sources: z.number(),
  required: z.number(),
  source_keys: z.array(SourceKeySchema),
  confirmed_count: z.number(),
  proposed_count: z.number(),
  shown_on_screen: z.boolean(),
  next_step: NextStepSchema,
});
export type AreaCoverage = z.infer<typeof AreaCoverageSchema>;
export type CoverageStatus = AreaCoverage['status'];

const CoverageSchema = z.object({
  framework_version: z.number(),
  sources_required: z.number(),
  corroboration_off: z.boolean(),
  untagged_confirmed: z.number(),
  areas: z.array(AreaCoverageSchema),
});
export type Coverage = z.infer<typeof CoverageSchema>;

const MatrixSchema = z.object({
  areas: z.array(
    z.object({
      area_id: z.string(),
      code: z.string(),
      label: z.string(),
      status: z.enum(['corroborated', 'partial', 'none']),
    }),
  ),
  sources: z.array(
    z.object({
      key: z.string(),
      kind: z.enum(['person', 'document']),
      id: z.string(),
      name: z.string(),
    }),
  ),
  cells: z.array(
    z.object({ area_id: z.string(), key: z.string(), confirmed: z.number(), proposed: z.number() }),
  ),
});
export type CoverageMatrix = z.infer<typeof MatrixSchema>;

const AreaRefSchema = z.object({ area_id: z.string(), code: z.string(), label: z.string() });
const CoverageRecordSchema = z.object({
  area_id: z.string(),
  code: z.string(),
  label: z.string(),
  status: z.string(),
  status_label: z.string(),
  sources: z.number(),
  required: z.number(),
});
export type CoverageRecord = z.infer<typeof CoverageRecordSchema>;

const VersionMovePreviewSchema = z.object({
  from: z.number(),
  to: z.number(),
  areas: z.object({
    added: z.array(AreaRefSchema),
    retired: z.array(AreaRefSchema),
    renamed: z.array(
      z.object({
        area_id: z.string(),
        before: z.object({ label: z.string() }),
        after: z.object({ label: z.string() }),
      }),
    ),
  }),
  rules: z.object({ sources_required: z.object({ from: z.number(), to: z.number() }) }),
  evidence_left_on_retired_areas: z.object({ stops_counting: z.number() }),
  coverage: z.object({
    before: z.array(CoverageRecordSchema),
    after: z.array(CoverageRecordSchema),
  }),
});
export type VersionMovePreview = z.infer<typeof VersionMovePreviewSchema>;

const MoveResultSchema = z.object({ framework_version: z.number(), from_version: z.number() });

// ---------------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------------

function base(engagementId: string): string {
  return `/api/v1/engagements/${encodeURIComponent(engagementId)}/discovery`;
}

export const getDiscoverySummary = (engagementId: string) =>
  apiClient(base(engagementId), DiscoverySummarySchema);

export const getDiscoverySetup = (engagementId: string) =>
  apiClient(`${base(engagementId)}/setup`, z.array(SetupFrameworkSchema));

export const startDiscovery = (engagementId: string, body: StartDiscoveryBody, key: string) =>
  apiClient(`${base(engagementId)}:start`, StartResultSchema, {
    method: 'POST',
    body,
    idempotencyKey: key,
  });

export const getCoverage = (engagementId: string) =>
  apiClient(`${base(engagementId)}/coverage`, CoverageSchema);

export const getCoverageMatrix = (engagementId: string) =>
  apiClient(`${base(engagementId)}/coverage/matrix`, MatrixSchema);

export const getVersionMove = (engagementId: string, to: number) =>
  apiClient(`${base(engagementId)}/version-move`, VersionMovePreviewSchema, { query: { to } });

export const moveFrameworkVersion = (
  engagementId: string,
  body: { to_version: number; from_version: number },
) =>
  apiClient(`${base(engagementId)}:move-version`, MoveResultSchema, {
    method: 'POST',
    body,
    idempotencyKey: newIdempotencyKey(),
  });

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export const discoveryKeys = {
  all: (engagementId: string) => ['discovery', engagementId] as const,
  summary: (engagementId: string) => ['discovery', engagementId, 'summary'] as const,
  setup: (engagementId: string) => ['discovery', engagementId, 'setup'] as const,
  coverage: (engagementId: string) => ['discovery', engagementId, 'coverage'] as const,
  matrix: (engagementId: string) => ['discovery', engagementId, 'matrix'] as const,
  move: (engagementId: string, to: number) => ['discovery', engagementId, 'move', to] as const,
  documents: (engagementId: string) => ['discovery', engagementId, 'documents'] as const,
};

/** `enabled` false skips the read (the engagement Overview, when Discovery is off). */
export function useDiscoverySummaryQuery(engagementId: string, enabled = true) {
  return useQuery({
    queryKey: discoveryKeys.summary(engagementId),
    queryFn: () => getDiscoverySummary(engagementId),
    enabled,
    staleTime: 15_000,
    // A module that is off answers not_found: retrying will not change it.
    retry: false,
  });
}

export function useDiscoverySetupQuery(engagementId: string, enabled = true) {
  return useQuery({
    queryKey: discoveryKeys.setup(engagementId),
    queryFn: () => getDiscoverySetup(engagementId),
    enabled,
    retry: false,
  });
}

export function useCoverageQuery(engagementId: string, enabled = true) {
  return useQuery({
    queryKey: discoveryKeys.coverage(engagementId),
    queryFn: () => getCoverage(engagementId),
    enabled,
    staleTime: 15_000,
    retry: false,
  });
}

export function useCoverageMatrixQuery(engagementId: string, enabled = true) {
  return useQuery({
    queryKey: discoveryKeys.matrix(engagementId),
    queryFn: () => getCoverageMatrix(engagementId),
    enabled,
    staleTime: 15_000,
    retry: false,
  });
}

export function useVersionMoveQuery(engagementId: string, to: number | null) {
  return useQuery({
    queryKey: discoveryKeys.move(engagementId, to ?? 0),
    queryFn: () => getVersionMove(engagementId, to!),
    enabled: to !== null,
    staleTime: 0,
    retry: false,
  });
}

/** The engagement's documents, for citing one as a client fact's source. No polling. */
export function useDiscoveryDocumentsQuery(engagementId: string, enabled: boolean) {
  return useQuery({
    queryKey: discoveryKeys.documents(engagementId),
    queryFn: () => listEngagementDocuments(engagementId),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

export function useStartDiscoveryMutation(engagementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { body: StartDiscoveryBody; key: string }) =>
      startDiscovery(engagementId, input.body, input.key),
    onSuccess: () => qc.invalidateQueries({ queryKey: discoveryKeys.all(engagementId) }),
  });
}

export function useMoveVersionMutation(engagementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { to_version: number; from_version: number }) =>
      moveFrameworkVersion(engagementId, body),
    onSettled: () => qc.invalidateQueries({ queryKey: discoveryKeys.all(engagementId) }),
  });
}
